import { useEffect, useState, useCallback } from "react";
import {
    getPendingUploads,
    pendingUploadCount,
} from "./offline";

/**
 * iter-58 — `useNetworkStatus` returns the live navigator.onLine state.
 *
 * Strictly informational — call sites should NEVER block capture or
 * upload based on this value (per spec). It exists so:
 *   • The offline indicator pill can render "Offline" when relevant.
 *   • Toasts can mention "back online" when the user reconnects.
 *
 * SSR-safe: returns `true` when `navigator` is undefined.
 */
export function useNetworkStatus() {
    const [online, setOnline] = useState(() =>
        typeof navigator === "undefined" ? true : navigator.onLine,
    );
    useEffect(() => {
        const handleOnline = () => setOnline(true);
        const handleOffline = () => setOnline(false);
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, []);
    return online;
}

/**
 * iter-58 — `useOfflineQueue({ projectId })` keeps a live count of
 * pending uploads scoped to a project (or globally when projectId is
 * omitted). Re-renders when the queue mutates in this tab via the
 * custom 'onsetflow:offline-queue-changed' event, OR when another tab
 * mutates localStorage (the native 'storage' event).
 */
export function useOfflineQueue({ projectId } = {}) {
    const [items, setItems] = useState(() => getPendingUploads({ project_id: projectId }));

    const refresh = useCallback(() => {
        setItems(getPendingUploads({ project_id: projectId }));
    }, [projectId]);

    useEffect(() => {
        refresh();
        const handler = () => refresh();
        // iter-58 nit: only react to storage events for OUR key so other
        // tabs writing unrelated localStorage entries don't trigger a
        // redundant re-render.
        const storageHandler = (e) => {
            if (!e || !e.key || e.key === "onsetflow:pending-uploads:v1") {
                refresh();
            }
        };
        window.addEventListener("onsetflow:offline-queue-changed", handler);
        window.addEventListener("storage", storageHandler);
        return () => {
            window.removeEventListener("onsetflow:offline-queue-changed", handler);
            window.removeEventListener("storage", storageHandler);
        };
    }, [refresh]);

    return {
        items,
        count: items.length,
        refresh,
        // Expose count helper directly so consumers can also poll without
        // subscribing to the live array (e.g. inside a useMemo).
        peekCount: () => pendingUploadCount({ project_id: projectId }),
    };
}
